import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import {
  saleEventPath,
  saleEventPhase,
  saleEventYear,
  type SaleEvent,
  type SaleEventPhase,
} from '@/seo/saleEvents';
import { saleEventDates } from '@/seo/templates';

const PHASE_BADGE: Record<SaleEventPhase, { text: string; color: 'primary' | 'success' | 'neutral' }> = {
  upcoming: { text: 'Upcoming', color: 'primary' },
  live: { text: 'Live now', color: 'success' },
  ended: { text: 'Ended', color: 'neutral' },
};

export interface SaleEventCardProps {
  event: SaleEvent;
  now: Date;
  locale?: Locale;
}

/**
 * One event as a plain link to its hub: "{Event} Sale {YYYY}", its other names,
 * its dates and where it stands. Used on the sale calendar, the home page and
 * the other hubs, always under an `<h2>`, so the name is an `<h3>`.
 */
export function SaleEventCard({ event, now, locale = DEFAULT_LOCALE }: SaleEventCardProps) {
  const badge = PHASE_BADGE[saleEventPhase(event, now, locale)];
  const singleDay = event.startDate === event.endDate;
  const dates = saleEventDates(event, { locale, t: useTranslate() });
  return (
    <Link
      to={saleEventPath(event, locale)}
      className="flex h-full flex-col gap-1 rounded-2xl border border-card-border bg-card p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-extrabold tracking-tight text-ink">
          {event.name} Sale {saleEventYear(event)}
        </h3>
        <Badge color={badge.color} className="shrink-0">
          {badge.text}
        </Badge>
      </div>
      {/* ink-secondary, not ink-tertiary: that is 2.5:1 on the light card and
          3.0:1 on the dark one, under the 4.5:1 small text needs. */}
      {event.aliases.length > 0 && (
        <p className="text-xs font-semibold text-ink-secondary">{event.aliases.join(' / ')}</p>
      )}
      <p className="mt-1 text-sm text-ink-secondary">
        {singleDay ? <time dateTime={event.startDate}>{dates}</time> : dates}
      </p>
    </Link>
  );
}

export default SaleEventCard;
